import { CSSProperties, ReactNode, forwardRef } from 'react';
import { createPortal } from 'react-dom';

import { FocusOn } from '../../libs/reactFocusOn';
import { DefaultComponentProps } from '../../types';

import { ModalContextProvider } from './context';
import { ContainerWithOverlay, ModalCard } from './Modal2.styled';
import { ModalVariant } from './types';

export type Modal2Props = DefaultComponentProps & {
  /**
   * **info** - usually used to prevent user from interrupting the desired flow. \
   * **form** - for modals that have some form inputs to enter. \
   * **complex** - for more complex scenarios (ex. more advanced form and such)
   */
  variant: ModalVariant;
  isOpen: boolean;
  onClose: () => void;
  children?: ReactNode;
  style?: CSSProperties;
};

const Modal2 = forwardRef<HTMLDivElement, Modal2Props>(
  (props, forwardedRef) => {
    const { isOpen, onClose, children, variant, ...rest } = props;

    if (!isOpen) return null;

    return createPortal(
      <ModalContextProvider onClose={onClose}>
        <ContainerWithOverlay>
          <FocusOn
            allowPinchZoom={true}
            autoFocus={false}
            onClickOutside={onClose}
            onEscapeKey={onClose}>
            <ModalCard
              $variant={variant}
              aria-modal="true"
              ref={forwardedRef}
              role="dialog"
              onClick={e => {
                e.stopPropagation();
              }}
              {...rest}>
              {children}
            </ModalCard>
          </FocusOn>
        </ContainerWithOverlay>
      </ModalContextProvider>,
      document.body,
    );
  },
);

Modal2.displayName = 'Modal2';

export default Modal2;
